/**
 * S-50 燃料費一覧（F-EXP-01）。kintone: 燃油・消耗品_一覧（速報_油代）
 * 速報1件＝1行で燃油代・潤滑油・現像代を一括編集し、「登録」で一括更新する。
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { CalendarCheck2, Eraser, Fuel, Save } from 'lucide-react';
import {
  Button,
  Checkbox,
  DateField,
  EmptyState,
  ErrorState,
  FormField,
  Loading,
  Modal,
  PageHeader,
  Panel,
  RadioGroup,
  SearchPanel,
  Textarea,
  TextField,
  useConfirm,
  useToast,
} from '@/components';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { errorMessage } from '@/lib/api';
import { formatNumber } from '@/lib/format';
import { cx } from '@/lib/cx';
import { CellDate, CellNumber, CellText, RoundingToggle } from '../common/cells';
import { Money, OperatorSelect, SummaryStrip } from '../common/components';
import { useOperatorScope } from '../common/useOperatorScope';
import { UNSAVED_MESSAGE, useUnsavedChangesGuard } from '../common/useUnsavedChangesGuard';
import { useFuelCalc, useFuelExpenses, usePreviewFuel, useSaveFuel, type FuelFilter, type FuelRowDto, type FuelSaveItem } from './api';

type SearchForm = Required<Omit<FuelFilter, 'businessOperatorId'>> & { businessOperatorId: number | null };

const EMPTY: SearchForm = {
  businessOperatorId: null,
  vesselName: '',
  surveyFrom: '',
  surveyTo: '',
  scheduledFrom: '',
  scheduledTo: '',
  paidFrom: '',
  paidTo: '',
  fuelPaid: '両方',
  sort: '参加船名簿の順',
};

function useFilter(): [SearchForm, (f: SearchForm) => void] {
  const [params, setParams] = useSearchParams();
  const filter = useMemo<SearchForm>(() => {
    const op = params.get('businessOperatorId');
    return {
      ...EMPTY,
      ...Object.fromEntries([...params.entries()].filter(([k]) => k in EMPTY)),
      businessOperatorId: op ? Number(op) : null,
    } as SearchForm;
  }, [params]);
  const set = (f: SearchForm) => {
    const next = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v != null && v !== '' && next.set(k, String(v)));
    setParams(next, { replace: true });
  };
  return [filter, set];
}

const toSave = (r: FuelRowDto): FuelSaveItem => ({
  voyageReportId: r.voyageReportId,
  fuelDate: r.fuelDate,
  fuelTaxRounding: r.fuelTaxRounding,
  fuelQuantity: r.fuelQuantity,
  fuelAmountExclTax: r.fuelAmountExclTax,
  fuelTax: r.fuelTax,
  fuelTotal: r.fuelTotal,
  fuelNote: r.fuelNote,
  fuelSubsidyScheduledOn: r.fuelSubsidyScheduledOn,
  fuelSubsidyPaidOn: r.fuelSubsidyPaidOn,
  lubeUnitPrice: r.lubeUnitPrice,
  lubeAmountExclTax: r.lubeAmountExclTax,
  lubeSubsidyScheduledOn: r.lubeSubsidyScheduledOn,
  lubeSubsidyPaidOn: r.lubeSubsidyPaidOn,
  devDate: r.devDate,
  devTaxRounding: r.devTaxRounding,
  devAmountExclTax: r.devAmountExclTax,
  devTax: r.devTax,
  devTotal: r.devTotal,
  devSubsidyScheduledOn: r.devSubsidyScheduledOn,
  devSubsidyPaidOn: r.devSubsidyPaidOn,
});

const daysBetween = (a: string, b: string) => Math.round((Date.parse(b) - Date.parse(a)) / 86_400_000);

type BulkTarget = 'fuel' | 'lube' | 'dev';
type BulkItem = 'scheduled' | 'paid';
const BULK_FIELDS: Record<BulkTarget, Record<BulkItem, keyof FuelRowDto>> = {
  fuel: { scheduled: 'fuelSubsidyScheduledOn', paid: 'fuelSubsidyPaidOn' },
  lube: { scheduled: 'lubeSubsidyScheduledOn', paid: 'lubeSubsidyPaidOn' },
  dev: { scheduled: 'devSubsidyScheduledOn', paid: 'devSubsidyPaidOn' },
};

export function FuelExpensesPage() {
  const { year } = useFiscalYear();
  const toast = useToast();
  const confirm = useConfirm();
  const { isCoop, fixedOperatorId } = useOperatorScope();
  const [filter, setFilter] = useFilter();
  const operatorId = isCoop ? fixedOperatorId : filter.businessOperatorId;
  const query = useFuelExpenses(year, { ...filter, businessOperatorId: operatorId }, operatorId != null);
  const preview = usePreviewFuel();
  const save = useSaveFuel();
  const calc = useFuelCalc();
  const { control, register, handleSubmit, reset } = useForm<SearchForm>({ values: { ...filter, businessOperatorId: operatorId } });

  const [rows, setRows] = useState<FuelRowDto[]>([]);
  const [errorRows, setErrorRows] = useState<number[]>([]);
  const [reason, setReason] = useState<{ index: number; text: string } | null>(null);
  const [bulk, setBulk] = useState<{ targets: BulkTarget[]; items: BulkItem[]; date: string }>({ targets: ['fuel'], items: ['scheduled'], date: '' });

  useEffect(() => {
    setRows(query.data?.items ?? []);
    setErrorRows([]);
  }, [query.data]);

  const original = useMemo(() => new Map((query.data?.items ?? []).map((r) => [r.voyageReportId, JSON.stringify(toSave(r))])), [query.data]);
  const dirtyIds = rows.filter((r) => original.get(r.voyageReportId) !== JSON.stringify(toSave(r))).map((r) => r.voyageReportId);
  useUnsavedChangesGuard(dirtyIds.length > 0);

  const update = (index: number, patch: Partial<FuelRowDto>) =>
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const totals = useMemo(
    () =>
      rows.reduce(
        (t, r) => ({
          fuelAmountExclTax: t.fuelAmountExclTax + r.fuelAmountExclTax,
          fuelTax: t.fuelTax + r.fuelTax,
          fuelTotal: t.fuelTotal + r.fuelTotal,
          lube: t.lube + r.lubeAmountExclTax,
          devAmountExclTax: t.devAmountExclTax + r.devAmountExclTax,
          devTotal: t.devTotal + r.devTotal,
        }),
        { fuelAmountExclTax: 0, fuelTax: 0, fuelTotal: 0, lube: 0, devAmountExclTax: 0, devTotal: 0 },
      ),
    [rows],
  );

  const onSearch = async (v: SearchForm) => {
    if (!isCoop && v.businessOperatorId == null) {
      toast.error('事業実施者は必須です');
      return;
    }
    if (dirtyIds.length && !(await confirm({ title: '確認', message: UNSAVED_MESSAGE, confirmLabel: 'OK' }))) return;
    setFilter(v);
  };

  /** 給油日の変更（L-19：帰港日から4日以上は理由入力、帰港日より前はエラー） */
  const onFuelDate = (index: number, value: string | null) => {
    const r = rows[index]!;
    update(index, { fuelDate: value, fuelDays: value ? daysBetween(r.periodTo, value) : null });
    if (!value) return;
    const d = daysBetween(r.periodTo, value);
    if (d < 0) toast.error('給油日は帰港日以降の日付にして下さい。');
    else if (d >= 4) setReason({ index, text: r.fuelNote ?? '' });
  };

  const onQuantity = (index: number, value: number | null) => {
    const r = rows[index]!;
    update(index, { fuelQuantity: value, dailyAverage: r.periodDays > 0 ? Math.round((value ?? 0) / r.periodDays) : 0 });
  };

  const runCalc = async (index: number, kind: 'fuel' | 'lubricant' | 'development') => {
    const r = rows[index]!;
    try {
      if (kind === 'fuel') {
        const res = await calc.mutateAsync({ kind, voyageReportId: r.voyageReportId, fuelDate: r.fuelDate, total: r.fuelTotal, taxAmount: r.fuelTax || null, taxRounding: r.fuelTaxRounding });
        update(index, { fuelAmountExclTax: res.amountExclTax, fuelTax: res.tax });
      } else if (kind === 'lubricant') {
        const res = await calc.mutateAsync({ kind, voyageReportId: r.voyageReportId, fuelQuantity: r.fuelQuantity, unitPrice: r.lubeUnitPrice });
        update(index, { lubeAmountExclTax: res.amountExclTax, lubeTotal: res.total });
      } else {
        const res = await calc.mutateAsync({ kind, voyageReportId: r.voyageReportId, devDate: r.devDate, total: r.devTotal, taxAmount: r.devTax || null, taxRounding: r.devTaxRounding });
        update(index, { devAmountExclTax: res.amountExclTax, devTax: res.tax });
      }
    } catch (e) {
      toast.error('計算できませんでした', errorMessage(e));
    }
  };

  const applyBulk = async (clear: boolean) => {
    if (!clear && !bulk.date) return toast.error('一括設定用の日付が入力されていません！');
    if (!bulk.targets.length) return toast.error('油代、潤滑油、現像代が一つもチェックされていません！');
    if (!bulk.items.length) return toast.error('助成金支払予定日、助成金支払日が一つもチェックされていません！');
    if (!(await confirm({ title: '確認', message: clear ? '日付の一括クリア処理を開始しますか？' : '日付の一括設定処理を開始しますか？', confirmLabel: 'OK' }))) return;
    setRows((prev) =>
      prev.map((r) => {
        const next = { ...r } as Record<string, unknown>;
        for (const t of bulk.targets) {
          for (const it of bulk.items) {
            if (it === 'paid' && isCoop) continue;
            const key = BULK_FIELDS[t][it];
            if (clear) next[key] = null;
            else if (!next[key]) next[key] = bulk.date; // 未入力の行のみ設定（kintone 準拠）
          }
        }
        return next as unknown as FuelRowDto;
      }),
    );
  };

  const onSubmit = async () => {
    if (!rows.length) return;
    if (!(await confirm({ title: '登録の確認', message: '入力した油代の登録変更処理を開始しますか？', confirmLabel: 'OK' }))) return;
    const items = rows.map(toSave);
    try {
      const p = await preview.mutateAsync(items);
      if (p.errors.length) {
        setErrorRows(p.errors.map((e) => e.voyageReportId));
        toast.error('登録できませんでした', p.errors.map((e) => `${rows[e.rowIndex]?.vesselName ?? ''}：${e.message}`).join('\n'));
        return;
      }
      const warns = p.warnings.filter((w) => w.code !== 'FUEL_DELAY');
      if (warns.length) {
        const ok = await confirm({
          title: '警告',
          message: warns.map((w) => `${rows[w.rowIndex]?.vesselName ?? ''}（${rows[w.rowIndex]?.periodFrom ?? ''}）：${w.message}`).join('\n'),
          confirmLabel: '登録する',
        });
        if (!ok) return;
      }
      const confirmations = Object.fromEntries(p.confirmations.map((c) => [c, true]));
      const res = await save.mutateAsync({ items, confirmations });
      toast.success('登録更新しました。', `${formatNumber(res.updated)} 件`);
    } catch (e) {
      toast.error('保存できませんでした', errorMessage(e));
    }
  };

  const loading = query.isFetching || save.isPending;

  return (
    <>
      <PageHeader
        eyebrow="経費・支払"
        title="燃料費一覧"
        description="速報ごとの燃油代・潤滑油・現像代（消耗品費）を一覧で入力し、まとめて登録します。"
        meta={dirtyIds.length ? <span className="bs-badge bs-badge--warning">未登録の変更 {dirtyIds.length} 件</span> : undefined}
        actions={
          <Button variant="primary" icon={<Save size={16} />} onClick={() => void onSubmit()} loading={save.isPending || preview.isPending} disabled={!rows.length}>
            登録
          </Button>
        }
      />

      <SearchPanel onSubmit={handleSubmit(onSearch)} onReset={() => reset({ ...EMPTY, businessOperatorId: operatorId })} searching={query.isFetching} columns={4}>
        <FormField label="事業実施者" required htmlFor="fuel-operator">
          <Controller control={control} name="businessOperatorId" render={({ field }) => <OperatorSelect id="fuel-operator" year={year} value={field.value} onChange={field.onChange} />} />
        </FormField>
        <FormField label="船名" hint="船名・カナの部分一致">
          <TextField {...register('vesselName')} />
        </FormField>
        <FormField label="調査期間" span={2} hint="出港日 ≧ FROM かつ 帰港日 ≦ TO">
          <span className="bs-exp-range">
            <DateField {...register('surveyFrom')} />
            <span>〜</span>
            <DateField {...register('surveyTo')} />
          </span>
        </FormField>
        <FormField label="助成金支払予定日（油代）" span={2}>
          <span className="bs-exp-range">
            <DateField {...register('scheduledFrom')} />
            <span>〜</span>
            <DateField {...register('scheduledTo')} />
          </span>
        </FormField>
        <FormField label="助成金支払日（油代）" span={2}>
          <span className="bs-exp-range">
            <DateField {...register('paidFrom')} />
            <span>〜</span>
            <DateField {...register('paidTo')} />
          </span>
        </FormField>
        <FormField label="支払状況（油代）" span={2}>
          <Controller
            control={control}
            name="fuelPaid"
            render={({ field }) => (
              <RadioGroup name="fuelPaid" value={field.value} onChange={field.onChange} options={['支払済', '未支払', '両方'].map((v) => ({ value: v as SearchForm['fuelPaid'], label: v }))} />
            )}
          />
        </FormField>
        <FormField label="並び順" span={2}>
          <Controller
            control={control}
            name="sort"
            render={({ field }) => (
              <RadioGroup name="sort" value={field.value} onChange={field.onChange} options={['参加船名簿の順', '調査日の順'].map((v) => ({ value: v as SearchForm['sort'], label: v }))} />
            )}
          />
        </FormField>
      </SearchPanel>

      <Panel title="助成金支払日の一括設定" description="チェックした区分・項目のうち、未入力の日付に設定日付を入れます（クリアは全行の値を空にします）。">
        <div className="bs-exp-bulk">
          <div className="bs-exp-bulk__group">
            <span className="bs-exp-bulk__label">対象</span>
            <span className="bs-exp-row">
              {([
                ['fuel', '油代'],
                ['lube', '潤滑油'],
                ['dev', '現像代'],
              ] as const).map(([k, l]) => (
                <Checkbox
                  key={k}
                  label={l}
                  checked={bulk.targets.includes(k)}
                  onChange={(e) => setBulk((b) => ({ ...b, targets: e.target.checked ? [...b.targets, k] : b.targets.filter((x) => x !== k) }))}
                />
              ))}
            </span>
          </div>
          <div className="bs-exp-bulk__group">
            <span className="bs-exp-bulk__label">項目</span>
            <span className="bs-exp-row">
              <Checkbox label="助成金支払予定日" checked={bulk.items.includes('scheduled')} onChange={(e) => setBulk((b) => ({ ...b, items: e.target.checked ? [...b.items, 'scheduled'] : b.items.filter((x) => x !== 'scheduled') }))} />
              {!isCoop && (
                <Checkbox label="助成金支払日" checked={bulk.items.includes('paid')} onChange={(e) => setBulk((b) => ({ ...b, items: e.target.checked ? [...b.items, 'paid'] : b.items.filter((x) => x !== 'paid') }))} />
              )}
            </span>
          </div>
          <div className="bs-exp-bulk__group">
            <label className="bs-exp-bulk__label" htmlFor="fuel-bulk-date">
              設定日付
            </label>
            <DateField id="fuel-bulk-date" value={bulk.date} onChange={(e) => setBulk((b) => ({ ...b, date: e.target.value }))} />
          </div>
          <span className="bs-exp-row">
            <Button icon={<CalendarCheck2 size={16} />} onClick={() => void applyBulk(false)} disabled={!rows.length}>
              支払日一括設定
            </Button>
            <Button variant="ghost" icon={<Eraser size={16} />} onClick={() => void applyBulk(true)} disabled={!rows.length}>
              支払日一括クリア
            </Button>
          </span>
        </div>
      </Panel>

      {operatorId == null ? (
        <EmptyState icon={<Fuel />} title="事業実施者を選択して検索してください" />
      ) : query.error ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : query.isPending ? (
        <Loading block />
      ) : rows.length === 0 ? (
        <EmptyState icon={<Fuel />} title="該当する速報はありません" description="検索条件を変更してください。" />
      ) : (
        <div className="bs-exp-stack">
          <SummaryStrip
            items={[
              { label: '件数', value: formatNumber(rows.length) },
              { label: '燃油 金額(税抜)', value: <Money value={totals.fuelAmountExclTax} /> },
              { label: '燃油 消費税', value: <Money value={totals.fuelTax} /> },
              { label: '燃油 合計金額', value: <Money value={totals.fuelTotal} />, emphasis: true },
              { label: '潤滑油 金額', value: <Money value={totals.lube} /> },
              { label: '現像代 金額(税抜)', value: <Money value={totals.devAmountExclTax} /> },
              { label: '現像代 合計金額', value: <Money value={totals.devTotal} /> },
            ]}
          />
          <div className={cx('bs-exp-grid-wrap', loading && 'is-loading')}>
            <table className="bs-exp-grid" aria-label="燃料費一覧">
              <thead>
                <tr>
                  <th rowSpan={2} className="is-sticky" style={{ minWidth: 150 }}>
                    船名
                  </th>
                  <th rowSpan={2}>調査期間</th>
                  <th colSpan={12} className="bs-exp-grid__group bs-exp-grid__group--fuel">
                    燃油代
                  </th>
                  <th colSpan={5} className="bs-exp-grid__group bs-exp-grid__group--lube">
                    潤滑油
                  </th>
                  <th colSpan={8} className="bs-exp-grid__group bs-exp-grid__group--dev">
                    現像代（消耗品費）
                  </th>
                  <th rowSpan={2}>備考</th>
                </tr>
                <tr>
                  <th>給油日数</th>
                  <th>燃油種類</th>
                  <th>給油日</th>
                  <th>数量(ℓ)</th>
                  <th>日平均(ℓ)</th>
                  <th>金額(税抜)</th>
                  <th>消費税</th>
                  <th>合計金額</th>
                  <th>支払予定日</th>
                  <th>支払日</th>
                  <th>税算出</th>
                  <th />
                  <th className="is-sep">単価</th>
                  <th>金額</th>
                  <th>支払予定日</th>
                  <th>支払日</th>
                  <th />
                  <th className="is-sep">現像日</th>
                  <th>金額(税抜)</th>
                  <th>消費税</th>
                  <th>合計金額</th>
                  <th>支払予定日</th>
                  <th>支払日</th>
                  <th>税算出</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const name = `${r.vesselName} ${r.periodFrom}`;
                  return (
                    <tr key={r.voyageReportId} className={cx(dirtyIds.includes(r.voyageReportId) && 'is-dirty', errorRows.includes(r.voyageReportId) && 'has-error')}>
                      <td className="is-sticky">
                        <strong>{r.vesselName}</strong>
                      </td>
                      <td className="bs-num">
                        {r.periodFrom}〜{r.periodTo}
                      </td>
                      <td className={cx('is-right bs-num', (r.fuelDays ?? 0) >= 4 && 'bs-exp-warn')}>{r.fuelDays ?? ''}</td>
                      <td>{r.fuelType ?? ''}</td>
                      <td>
                        <CellDate label={`${name} 給油日`} value={r.fuelDate} invalid={r.fuelDays != null && r.fuelDays < 0} onChange={(v) => onFuelDate(i, v)} />
                      </td>
                      <td>
                        <CellNumber label={`${name} 燃油数量`} kind="qty" decimals={2} value={r.fuelQuantity} onChange={(v) => onQuantity(i, v)} />
                      </td>
                      <td className="is-right bs-num">{formatNumber(r.dailyAverage)}</td>
                      <td>
                        <CellNumber label={`${name} 燃油金額(税抜)`} value={r.fuelAmountExclTax} onChange={(v) => update(i, { fuelAmountExclTax: v ?? 0 })} />
                      </td>
                      <td>
                        <CellNumber label={`${name} 燃油消費税`} value={r.fuelTax} onChange={(v) => update(i, { fuelTax: v ?? 0 })} />
                      </td>
                      <td>
                        <CellNumber label={`${name} 燃油合計金額`} value={r.fuelTotal} onChange={(v) => update(i, { fuelTotal: v ?? 0 })} />
                      </td>
                      <td>
                        <CellDate label={`${name} 燃油支払予定日`} value={r.fuelSubsidyScheduledOn} onChange={(v) => update(i, { fuelSubsidyScheduledOn: v })} />
                      </td>
                      <td>
                        <CellDate label={`${name} 燃油支払日`} value={r.fuelSubsidyPaidOn} disabled={isCoop} onChange={(v) => update(i, { fuelSubsidyPaidOn: v })} />
                      </td>
                      <td>
                        <RoundingToggle label={`${name} 燃油税算出`} value={r.fuelTaxRounding} onChange={(v) => update(i, { fuelTaxRounding: v })} />
                      </td>
                      <td>
                        <Button size="sm" onClick={() => void runCalc(i, 'fuel')}>
                          消費税
                        </Button>
                      </td>
                      <td className="is-sep">
                        <CellNumber label={`${name} 潤滑油単価`} decimals={2} value={r.lubeUnitPrice} onChange={(v) => update(i, { lubeUnitPrice: v })} />
                      </td>
                      <td>
                        <CellNumber label={`${name} 潤滑油金額`} value={r.lubeAmountExclTax} onChange={(v) => update(i, { lubeAmountExclTax: v ?? 0 })} />
                      </td>
                      <td>
                        <CellDate label={`${name} 潤滑油支払予定日`} value={r.lubeSubsidyScheduledOn} onChange={(v) => update(i, { lubeSubsidyScheduledOn: v })} />
                      </td>
                      <td>
                        <CellDate label={`${name} 潤滑油支払日`} value={r.lubeSubsidyPaidOn} disabled={isCoop} onChange={(v) => update(i, { lubeSubsidyPaidOn: v })} />
                      </td>
                      <td>
                        <Button size="sm" onClick={() => void runCalc(i, 'lubricant')}>
                          計算
                        </Button>
                      </td>
                      <td className="is-sep">
                        <CellDate label={`${name} 現像日`} value={r.devDate} onChange={(v) => update(i, { devDate: v })} />
                      </td>
                      <td>
                        <CellNumber label={`${name} 現像代金額(税抜)`} value={r.devAmountExclTax} onChange={(v) => update(i, { devAmountExclTax: v ?? 0 })} />
                      </td>
                      <td>
                        <CellNumber label={`${name} 現像代消費税`} value={r.devTax} onChange={(v) => update(i, { devTax: v ?? 0 })} />
                      </td>
                      <td>
                        <CellNumber label={`${name} 現像代合計金額`} value={r.devTotal} onChange={(v) => update(i, { devTotal: v ?? 0 })} />
                      </td>
                      <td>
                        <CellDate label={`${name} 現像代支払予定日`} value={r.devSubsidyScheduledOn} onChange={(v) => update(i, { devSubsidyScheduledOn: v })} />
                      </td>
                      <td>
                        <CellDate label={`${name} 現像代支払日`} value={r.devSubsidyPaidOn} disabled={isCoop} onChange={(v) => update(i, { devSubsidyPaidOn: v })} />
                      </td>
                      <td>
                        <RoundingToggle label={`${name} 現像代税算出`} value={r.devTaxRounding} onChange={(v) => update(i, { devTaxRounding: v })} />
                      </td>
                      <td>
                        <Button size="sm" onClick={() => void runCalc(i, 'development')}>
                          計算
                        </Button>
                      </td>
                      <td>
                        <CellText label={`${name} 備考`} value={r.fuelNote} onChange={(v) => update(i, { fuelNote: v })} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td className="is-sticky">合計</td>
                  <td colSpan={6} />
                  <td className="is-right"><Money value={totals.fuelAmountExclTax} /></td>
                  <td className="is-right"><Money value={totals.fuelTax} /></td>
                  <td className="is-right"><Money value={totals.fuelTotal} /></td>
                  <td colSpan={5} />
                  <td className="is-right"><Money value={totals.lube} /></td>
                  <td colSpan={4} />
                  <td className="is-right"><Money value={totals.devAmountExclTax} /></td>
                  <td />
                  <td className="is-right"><Money value={totals.devTotal} /></td>
                  <td colSpan={5} />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      <Modal
        open={reason != null}
        onClose={() => setReason(null)}
        title="給油が遅れた理由"
        size="md"
        footer={
          <>
            <Button onClick={() => setReason(null)}>キャンセル</Button>
            <Button
              variant="primary"
              onClick={() => {
                if (reason) update(reason.index, { fuelNote: reason.text || null });
                setReason(null);
              }}
            >
              備考に設定
            </Button>
          </>
        }
      >
        <p>帰港日から日数が経過しています。給油が遅れた理由を以下にご入力ください。</p>
        <Textarea rows={4} aria-label="給油が遅れた理由" value={reason?.text ?? ''} onChange={(e) => setReason((r) => (r ? { ...r, text: e.target.value } : r))} />
      </Modal>
    </>
  );
}
